import { I18nUtil } from "@utils/i18n";
import NotificationSignalHeaderEventTypeTranslations from "@translate/components/Notification/NotificationSignalHeaderEventType";
import type { NotificationSignalHeaderLabelItem } from "./NotificationSignalHeaderLabel";
import { buildNotificationSignalHeaderLabelItems } from "./notificationSignalHeaderLabelItems";
import { rootStore } from "@store";
import { useCredits } from "@/src/hooks/useCredits";
import { useMemo } from "react";
import { useNotifyHoldingsSignalsInfinite } from "@hooks";
import { useParams } from "next/navigation";
import { useSnapshot } from "valtio";

const HOLDINGS_PAGE_LIMIT = 10;

export type UseNotificationSignalHeaderResult =
  | { status: "hidden" }
  | {
      status: "ready";
      /** credit <= 0 → show Activate Ed header */
      needActive: boolean;
      labelItems: NotificationSignalHeaderLabelItem[];
      totalSignals: number;
    };

/**
 * Signal header visibility (credit exhausted only):
 * - Credits from `useCredits` only (no separate member gating).
 * - credit <= 0 → show header when holdings rows exist (needActive).
 * - No loading UI: hidden until credits + holdings are ready, or if no rows / error.
 */
export function useNotificationSignalHeader(): UseNotificationSignalHeaderResult {
  const params = useParams();
  const locale = (params?.locale as string) || "en";

  const { ownerInfo } = useSnapshot(rootStore.user);
  const ownerId = ownerInfo?.id ?? "";

  const { credits, isLoading: creditsLoading } = useCredits();

  const holdingsQueryParams = useMemo(
    () => ({ limit: HOLDINGS_PAGE_LIMIT }),
    [],
  );

  const holdingsEnabled = !!ownerId;

  const holdings = useNotifyHoldingsSignalsInfinite({
    queryParams: holdingsQueryParams,
    enabled: holdingsEnabled,
  });
  const rows = useMemo(() => holdings.rows ?? [], [holdings.rows]);
  const holdingsPendingFirstPaint =
    holdingsEnabled &&
    (holdings.isLoading || (holdings.isFetching && rows.length === 0));

  const labelItems = useMemo(() => {
    const I18n = I18nUtil.createScoped(
      NotificationSignalHeaderEventTypeTranslations,
      locale,
    );
    return buildNotificationSignalHeaderLabelItems(rows, (key) => I18n.t(key));
  }, [rows, locale]);

  const needActive = credits <= 0;

  if (!ownerId) {
    return { status: "hidden" };
  }

  if (creditsLoading) {
    return { status: "hidden" };
  }

  if (holdingsPendingFirstPaint) {
    return { status: "hidden" };
  }

  if (holdings.error) {
    return { status: "hidden" };
  }

  if (rows.length === 0) {
    return { status: "hidden" };
  }

  if (!needActive) {
    return { status: "hidden" };
  }

  return {
    status: "ready",
    needActive,
    labelItems,
    totalSignals: rows.length,
  };
}
